import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { usePendingEdits } from "@/lib/pending-edits";

// Floating toast at the bottom: appears as soon as any scope has unsaved
// changes. Centralised Verwerfen + Speichern for the whole app.
// Ported node-by-node from workshop/shell/SaveBar.jsx.

export function SaveBar() {
  const intl = useIntl();
  const ctx = usePendingEdits();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const dirty = Object.values(ctx?.scopes ?? {}).filter((s) => s.dirty);
  if (dirty.length === 0) return null;

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await Promise.all(dirty.map((s) => s.save()));
      // Signal to inline-edit primitives (e.g. EditableRow) that they can
      // auto-collapse - they subscribe to ctx.saveTick.
      ctx?.signalSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : intl.formatMessage({ id: "common.saveFailed", defaultMessage: "Save failed." }));
    } finally {
      setSaving(false);
    }
  };

  const onDiscard = () => {
    setError("");
    dirty.forEach((s) => s.discard());
  };

  // Entries errors ship as "summary\nreason" (use-profile-entries-state.ts); single-line
  // errors just leave `errorDetail` empty.
  const [errorSummary, ...errorDetailParts] = (error ?? "").split("\n");
  const errorDetail = errorDetailParts.join(" ");

  return (
    <div className="fixed bottom-4 sm:bottom-8 left-1/2 z-[900] w-[36rem] max-w-[calc(100vw-2rem)] -translate-x-1/2">
      <div className="smycles-toast pr-2">
        <span className="min-w-0 flex-1">
          {/* Error inherits the on-accent ink: the toast surface IS the
              signal color, so a text-accent span would be invisible. */}
          {error ? (
            <span className="flex flex-col">
              <span className="font-medium">{errorSummary}</span>
              {errorDetail ? <span className="text-[11px] opacity-80">{errorDetail}</span> : null}
            </span>
          ) : (
            intl.formatMessage({ id: "unsavedGuard.title", defaultMessage: "Unsaved changes" })
          )}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            onClick={onDiscard}
            disabled={saving}
            className="px-3 py-1 text-[10px] disabled:pointer-events-none disabled:opacity-50"
          >
            {intl.formatMessage({ id: "common.discard", defaultMessage: "Discard" })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={saving}
            className="px-3 py-1 text-[10px] disabled:pointer-events-none disabled:opacity-50"
          >
            {saving
              ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
              : intl.formatMessage({ id: "common.save", defaultMessage: "Save" })}
          </Button>
        </div>
      </div>
    </div>
  );
}
